import { v1 as createUuid } from 'uuid'

import {
  IFileRequisites,
  SourceEntityType,
  UploadingType,
} from '~/client/graph'

import UploadFileCommand from '../../commands/UploadFile/UploadFileCommand'
import UploadFileToMapboxS3Command, {
  UploadProgressCallback,
} from '../../commands/UploadFile/UploadFileToMapboxS3Command'
import MapboxService, { IS3Config } from '../../services/MapboxService'
import GraphExecutorStore from './GraphExecutor.store'

export const msWordExtensions = [
  'doc',
  'docx',
  'dot',
  'dotx',
  'docm',
  'dotm',
  'vnd.openxmlformats-officedocument.wordprocessingml.document',
]
export const msExcelExtensions = [
  'xls',
  'xlsx',
  'xlsm',
  'xlsb',
  'xltx',
  'xltm',
  'xlam',
  'csv',
  'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]
export const msPowerPointExtensions = [
  'ppt',
  'pptx',
  'pptm',
  'potx',
  'potm',
  'ppam',
  'ppsx',
  'ppsm',
  'sldx',
  'sldm',
  'vnd.openxmlformats-officedocument.presentationml.presentation',
]
export const pdfExtension = 'pdf'
export const imageExtensions = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'tiff',
  'tif',
  'svg',
  'webp',
  'ico',
  'octet-stream',
  'svg+xml',
]
export const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'quicktime']
const logUploadExtensions = [
  UploadingType.Image,
  UploadingType.Pdf,
  UploadingType.MsExcel,
  UploadingType.MsWord,
  UploadingType.MsPowerPoint,
  UploadingType.AttachmentFile,
]
const publicUploadExtensions = [
  UploadingType.Image,
  UploadingType.Pdf,
  UploadingType.Avatar,
  UploadingType.MsExcel,
  UploadingType.MsWord,
  UploadingType.MsPowerPoint,
  UploadingType.AttachmentFile,
]
export class FileUploadingStore {
  public constructor(
    private graphExecutorStore: GraphExecutorStore,
    private readonly mapboxService: MapboxService,
  ) {}

  public getAttachmentFileType(file: File): UploadingType {
    const extension = file.name?.split('.')?.pop()?.toLowerCase()
    switch (true) {
      case msWordExtensions.includes(extension):
        return UploadingType.MsWord
      case msPowerPointExtensions.includes(extension):
        return UploadingType.MsPowerPoint
      case msExcelExtensions.includes(extension):
        return UploadingType.MsExcel
      case extension === pdfExtension:
        return UploadingType.Pdf
      case imageExtensions.includes(extension):
        return UploadingType.Image
      default:
        return UploadingType.AttachmentFile
    }
  }

  public async uploadFile(
    file: Blob,
    fileType: UploadingType,
    projectId: string,
    fileName?: string,
    omitLogCreation?: boolean,
    sourceEntityId?: string,
    sourceEntityType?: SourceEntityType,
    locationIds?: string[],
    sourceEntityCode?: string,
    sourceEntityTypeName?: string,
    locationName?: string,
    isLogHidden?: boolean,
  ): Promise<IFileRequisites[]> {
    if (!fileName) {
      fileName = createUuid()
    }

    const isFilePublic = publicUploadExtensions.includes(fileType)

    const isLogUpload =
      logUploadExtensions.includes(fileType) && !omitLogCreation

    const command = new UploadFileCommand(
      file,
      this.graphExecutorStore,
      fileName,
      fileType,
      isFilePublic,
      projectId,
      sourceEntityId,
      sourceEntityType,
      locationIds,
      sourceEntityCode,
      sourceEntityTypeName,
      locationName,
      isLogUpload,
      isLogHidden,
    )

    return command.execute()
  }

  public async uploadFileToMapboxS3(
    file: Blob,
    onProgress?: UploadProgressCallback,
    onAbortReady?: (abort: () => void) => void,
  ): Promise<IS3Config> {
    const command = new UploadFileToMapboxS3Command(file, this.mapboxService)

    if (onAbortReady) {
      onAbortReady(() => command.abort())
    }

    return command.execute(onProgress)
  }
}
